import { Stack, Typography } from '@mui/material'
import { FC, useMemo } from 'react'

import { AddonProductKinds } from 'constants/product/productTypes'
import { AssignmentDTO } from 'models/assignment'
import { DealDTO } from 'models/deal'
import { DetailField, DetailSection } from '../../common'
import { FeeDTO } from 'models/fee'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { applyFeeDiscount, valueMultiplied } from 'utils/price/priceCalculations'
import { formatPrice } from 'utils/price/priceFormatter'
import { useTranslation } from 'react-i18next'

interface Props {
  /** Assignment whose products are scanned for add-ons */
  assignment: AssignmentDTO
  /** Parent order — used to look up the unit price for each add-on */
  order: DealDTO | null
  /** ID of the main product — excluded from the add-on list if it happens to also match an add-on kind */
  mainProductId?: number
}

/**
 * Renders the list of add-on products purchased alongside the main product on the assignment.
 * Hidden when there are no add-ons.
 */
export const AssignmentAddonsSection: FC<Props> = ({ assignment, order, mainProductId }) => {
  const { t } = useTranslation(['gallery', 'product', 'product_kind', 'category'])

  const priceByProductId = useMemo(() => {
    const map = new Map<number, FeeDTO>()

    for (const p of order?.financials.products ?? []) {
      if (!map.has(p.productId)) map.set(p.productId, p.unitPrice)
    }

    return map
  }, [order?.financials.products])

  const addons = useMemo(() => {
    const byId = new Map<number, { id: number, kind: string, category: string, quantity: number }>()

    for (const p of assignment.products) {
      if (p.id === mainProductId) continue
      if (!AddonProductKinds.has(p.kind)) continue

      const existing = byId.get(p.id)

      if (existing) {
        existing.quantity += p.quantity
      } else {
        byId.set(p.id, { id: p.id, kind: p.kind, category: p.category, quantity: p.quantity })
      }
    }

    return Array.from(byId.values())
  }, [assignment.products, mainProductId])

  if (!addons.length) return null

  return (
    <DetailSection title={t('gallery:assignment_details.addons')}>
      <DetailField>
        <Stack gap={1.2}>

          {addons.map(a => {
            const name = t(`product:p_${a.id}`,
              t(`product_kind:${a.kind}`,
                t(`category:${a.category}`)
              )
            )

            const unitPrice = priceByProductId.get(a.id)
            const discountedUnitPrice = unitPrice ? applyFeeDiscount(unitPrice) : undefined
            const formattedUnitPrice = discountedUnitPrice ? formatPrice(discountedUnitPrice) : undefined
            const displayPrice = discountedUnitPrice && unitPrice
              ? formatPrice(valueMultiplied(discountedUnitPrice, a.quantity), unitPrice.currency)
              : undefined

            return (
              <Stack key={a.id} direction="row" justifyContent="space-between" gap={1.6}>
                <Stack direction="column" alignItems="flex-start">
                  <Typography color={GRAY_900} fontWeight={450}>
                    {a.quantity > 1 ? `${a.quantity} × ${name}` : name}
                  </Typography>

                  {a.quantity > 1 && !!formattedUnitPrice &&
                    <Typography color={GRAY_700} fontSize="1rem">
                      {t('gallery:assignment_details.unit_price')} {formattedUnitPrice}
                    </Typography>
                  }
                </Stack>

                {!!displayPrice &&
                  <Typography color={GRAY_700} fontWeight={550}>
                    {displayPrice}
                  </Typography>
                }
              </Stack>
            )
          })}

        </Stack>
      </DetailField>
    </DetailSection>
  )
}
